/**
 * DossierPage — Hồ sơ dự thi: gộp Đơn khởi kiện + Chứng cứ vào tabs chung.
 *
 * Audit 2026-05-21 (F.3): trước đây là stub link sang /memorials và /evidence.
 * Giờ embed trực tiếp 2 page con vào 2 tabs để user không phải navigate giữa
 * các trang riêng. Bảng phân vai đã tách sang /role-sheets (gate theo R1 status).
 *
 * R1 flow versioning 2026-05-21: khi user đang trong case dùng flow
 * `dual_claimant_packets` thì render R1SubmissionPage thay vì 2 tab cũ. Quyết
 * định dựa trên cuộc thi mà đội user thuộc — nếu có ít nhất 1 dual case →
 * dùng layout mới (tabs scenario_a/scenario_b).
 */
import { lazy, Suspense, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from '@mui/material'
import { Description, AttachFile } from '@mui/icons-material'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useAppSelector } from '../hooks/redux'

const MemorialsPage = lazy(() => import('./MemorialsPage'))
const EvidencePage = lazy(() => import('./EvidencePage'))
const R1SubmissionPage = lazy(() => import('./R1SubmissionPage'))

type TabKey = 'memorial' | 'evidence'

const TAB_DEFS: Array<{ key: TabKey; label: string; icon: React.ReactElement }> = [
  { key: 'memorial', label: 'Đơn khởi kiện', icon: <Description /> },
  { key: 'evidence', label: 'Chứng cứ', icon: <AttachFile /> },
]

function TabFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress />
    </Box>
  )
}

export default function DossierPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialTab = (searchParams.get('tab') as TabKey) || 'memorial'
  const [tab, setTab] = useState<TabKey>(initialTab)

  // Detect flow R1 mới — nếu đội user (hoặc bất kỳ case nào nhìn thấy) dùng
  // `dual_claimant_packets` → ưu tiên layout mới.
  const { user: currentUser } = useAppSelector((s) => s.auth)
  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [] } = useGetTeamsQuery()
  const { useDualClaimantLayout, isMixCase } = useMemo(() => {
    const userTeam = teams.find((t) => t.members.includes(currentUser?.id || ''))
    if (userTeam) {
      const teamCases = cases.filter((c) =>
        (c.assigned_team_ids || []).includes(userTeam.id),
      )
      const dualCount = teamCases.filter(
        (c) => c.r1_flow_version === 'dual_claimant_packets',
      ).length
      // #17 (audit P2): cảnh báo team có CẢ dual + legacy case → dual layout
      // không hỗ trợ render legacy memorial/evidence.
      const mix = dualCount > 0 && dualCount < teamCases.length
      return { useDualClaimantLayout: dualCount > 0, isMixCase: mix }
    }
    // BTC / judge: chỉ chuyển sang layout mới khi MỌI case visible đều dual.
    if (!cases.length) return { useDualClaimantLayout: false, isMixCase: false }
    return {
      useDualClaimantLayout: cases.every((c) => c.r1_flow_version === 'dual_claimant_packets'),
      isMixCase: false,
    }
  }, [cases, teams, currentUser?.id])

  if (useDualClaimantLayout) {
    return (
      <Suspense fallback={<TabFallback />}>
        {isMixCase && (
          <Container maxWidth="xl" sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Đội của bạn thuộc cả cuộc thi <strong>dual_claimant_packets</strong> (R1 mới)
              và <strong>legacy_single_role</strong>. Trang đang hiển thị layout R1 mới.
              Để xem hồ sơ legacy, vào trực tiếp <code>/memorials</code> hoặc <code>/evidence</code>.
            </Alert>
          </Container>
        )}
        <R1SubmissionPage />
      </Suspense>
    )
  }

  const handleTabChange = (_: React.SyntheticEvent, value: TabKey) => {
    setTab(value)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('tab', value)
      return next
    })
  }

  return (
    <Container maxWidth="xl" disableGutters sx={{ mt: 0, mb: 0 }}>
      {/* Tabs đặt ở top, không có PageHeader riêng — child page có header riêng */}
      <Paper
        elevation={0}
        square
        sx={(theme) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          mx: { xs: -2, sm: -3 },
          mt: { xs: -2, sm: -3 },
          mb: 0,
          px: { xs: 2, sm: 3 },
          backgroundColor: theme.palette.background.paper,
        })}
      >
        <Tabs value={tab} onChange={handleTabChange} variant="standard">
          {TAB_DEFS.map((t) => (
            <Tab
              key={t.key}
              value={t.key}
              icon={t.icon}
              iconPosition="start"
              label={t.label}
              sx={{ minHeight: 56, fontWeight: 600, textTransform: 'none' }}
            />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ pt: { xs: 2, sm: 3 } }}>
        <Suspense fallback={<TabFallback />}>
          {tab === 'memorial' && <MemorialsPage />}
          {tab === 'evidence' && <EvidencePage />}
        </Suspense>
      </Box>
    </Container>
  )
}
